import React, { useState } from "react";
import { registerUser, ensureSession } from "../authService";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { CelestialShell, SystemFooter } from "./ui/CelestialShell";

function Register() {
  const hostname = window.location.hostname;
  const devAccess = hostname === 'dev.brightresolve.ai' || (process.env.NODE_ENV === 'development' && ['localhost', '127.0.0.1'].includes(hostname));
  const [username, setUsername] = useState(""); // username required
  const [password, setPassword] = useState(""); // password required
  const [saveConversationHistory, setSaveConversationHistory] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { refreshAuth } = useAuth();

  const handleRegister = async (e) => {
    e.preventDefault();
    setMessage("");
    setLoading(true);
    try {
      await ensureSession();

      if (!username.trim() || !password.trim()) {
        setMessage("Enter a username and password.");
        return;
      }

      const { success, data } = await registerUser({
        username: username.trim(),
        password: password.trim(),
        tenantId: "default",
        saveConversationHistory,
      });

      if (success) {
        await refreshAuth();
        navigate(
          saveConversationHistory && data?.save_conversation_history !== true
            ? "/settings?history-setup=required"
            : "/chat",
          { replace: true },
        );
      } else {
        setMessage(data?.error || "Couldn’t complete that step. Please try again.");
      }
    } catch (err) {
      console.error("Registration error:", err);
      setMessage(err?.message || "Something interrupted the signal.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CelestialShell>
      <div className="br-container">
        <main className="br-auth-layout">
          <section className="br-auth-story">
            <p className="br-eyebrow">Begin with intention</p>
            <h1 className="br-page-title">A private place<br />to think clearly.</h1>
            <p className="br-lead">Create a space that moves at your pace, quiet, considered, and always under your control.</p>
          </section>

          <section className="br-auth-card br-glass">
            <header className="br-auth-card__header">
              <p className="br-eyebrow">Create your space</p>
              <h2 className="br-auth-card__title">Let’s begin something new.</h2>
              <p className="br-auth-card__copy">Choose the private credentials you’ll use to return.</p>
            </header>
            {devAccess ? <form onSubmit={handleRegister} className="br-auth-form" data-testid="developer-registration-form">
              <div className="br-field">
                <label htmlFor="br-register-username">Username</label>
                <input id="br-register-username" type="text" aria-label="Username" autoComplete="username" placeholder="Choose a username" value={username} onChange={(e) => setUsername(e.target.value)} disabled={loading} className="br-input" />
              </div>
              <label className="br-toggle" htmlFor="br-register-save-history">
                <span>
                  <strong className="br-toggle__label">Save conversation history</strong>
                  <span className="br-toggle__detail">Optional and off by default. Save new chats so you can reopen them later; you can turn this off or delete saved chats in Settings.</span>
                </span>
                <input id="br-register-save-history" type="checkbox" checked={saveConversationHistory} onChange={(event) => setSaveConversationHistory(event.target.checked)} disabled={loading} className="br-switch" />
              </label>
              <div className="br-field">
                <label htmlFor="br-register-password">Password</label>
                <input id="br-register-password" type="password" aria-label="Password" autoComplete="new-password" placeholder="Choose a password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} className="br-input" />
              </div>
              {message && <p className="br-status br-status--error" role="alert">{message}</p>}
              <button type="submit" disabled={loading} className="br-button br-button--primary">
                {loading ? "Creating your space..." : "Create private space"}
              </button>
              <div className="br-auth-links">
                <span>Already registered? <Link to="/login">Sign in</Link></span>
                <Link to="/">Return home</Link>
              </div>
            </form> : <div className="br-auth-gate" data-testid="registration-access-gate">
              <p>BrightResolve is currently available only to approved preview members.</p>
              <Link to="/login" className="br-button br-button--primary">Sign in</Link>
              <Link to="/contact" className="br-button">Contact BrightResolve</Link>
            </div>}
          </section>
        </main>
        <SystemFooter />
      </div>
    </CelestialShell>
  );
}

export default Register;
